import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaUsers,
  FaChair,
  FaClock,
  FaDollarSign,
  FaPlus,
  FaCalendar,
  FaBell,
  FaChartLine,
  FaCoffee,
} from "react-icons/fa";
import "./Staff.css";

function StaffDashboard() {
  // Animated counter hook
  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / duration;

        if (progress < 1) {
          setCount(Math.floor(end * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }, [end, duration]);

    return count;
  };

  // Counter values
  const totalCustomers = useCounter(248, 1500);
  const activeTables = useCounter(18, 1500);
  const revenue = useCounter(125, 1800); // 12.5M
  const hours = useCounter(8, 1200);

  // Table data
  const tables = [
    { id: "B01", status: "occupied", area: "A" },
    { id: "B02", status: "available", area: "A" },
    { id: "B03", status: "occupied", area: "A" },
    { id: "B04", status: "available", area: "A" },
    { id: "B05", status: "reserved", area: "B" },
    { id: "B06", status: "available", area: "B" },
    { id: "B07", status: "occupied", area: "B" },
    { id: "B08", status: "available", area: "B" },
    { id: "B09", status: "occupied", area: "C" },
    { id: "B10", status: "reserved", area: "C" },
    { id: "B11", status: "available", area: "C" },
    { id: "B12", status: "occupied", area: "C" },
  ];

  return (
    <Container fluid className="p-0">
      {/* Dashboard Header */}
      <div className="px-4 pt-4 pb-3 animate-fade-in">
        <Row className="align-items-center">
          <Col>
            <h2 className="mb-1" style={{ fontSize: "2rem", fontWeight: 700 }}>
              Dashboard Nhân Viên
            </h2>
            <p className="text-muted mb-0">
              <FaCoffee
                className="me-2"
                style={{ color: "var(--amber-500)" }}
              />
              Chào buổi sáng! Chúc bạn một ngày làm việc hiệu quả
            </p>
          </Col>
          <Col xs="auto">
            <button className="btn btn-dark-primary">
              <FaPlus className="me-2" />
              Thêm mới
            </button>
          </Col>
        </Row>
      </div>

      {/* Bento Grid Layout */}
      <div className="bento-grid">
        {/* Hero Card - Total Customers */}
        <div className="dark-card hero-card bento-item-2x2 animate-fade-in-up">
          <div className="d-flex flex-column h-100 justify-content-between">
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background:
                      "linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.05) 100%)",
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUsers
                    style={{ fontSize: "1.5rem", color: "var(--amber-400)" }}
                  />
                </div>
                <div>
                  <h6
                    className="mb-0 text-muted"
                    style={{ fontSize: "0.875rem", fontWeight: 500 }}
                  >
                    Tổng khách hàng hôm nay
                  </h6>
                  <small className="text-dim">Cập nhật realtime</small>
                </div>
              </div>
              <div className="stat-number animated-counter">
                {totalCustomers}
              </div>
              <div className="d-flex gap-2 mt-3">
                <span className="glow-badge success">
                  <span
                    className="pulse-indicator"
                    style={{ background: "var(--success)" }}
                  ></span>
                  +12% so với hôm qua
                </span>
              </div>
            </div>
            <div className="mt-4">
              <small className="text-muted">
                Ca sáng: 42 | Ca chiều: 38 | Ca tối: 168
              </small>
            </div>
          </div>
        </div>

        {/* Active Tables */}
        <div className="dark-card bento-item-1x1 animate-fade-in-up">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <p
                className="text-muted mb-2"
                style={{ fontSize: "0.875rem", fontWeight: 500 }}
              >
                Bàn đang dùng
              </p>
              <h2
                className="mb-0"
                style={{
                  fontSize: "2.5rem",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, var(--warning) 0%, var(--amber-600) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {activeTables}/30
              </h2>
              <span className="glow-badge warning mt-2">60%</span>
            </div>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "rgba(245, 158, 11, 0.1)",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaChair
                style={{ fontSize: "1.75rem", color: "var(--warning)" }}
              />
            </div>
          </div>
        </div>

        {/* Operating Hours */}
        <div className="dark-card bento-item-1x1 animate-fade-in-up">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <p
                className="text-muted mb-2"
                style={{ fontSize: "0.875rem", fontWeight: 500 }}
              >
                Giờ hoạt động
              </p>
              <h2
                className="mb-0"
                style={{
                  fontSize: "2.5rem",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, var(--success) 0%, #059669 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {hours}h 45m
              </h2>
              <span className="glow-badge success mt-2">
                <span
                  className="pulse-indicator"
                  style={{ background: "var(--success)" }}
                ></span>
                Đang mở
              </span>
            </div>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "rgba(16, 185, 129, 0.1)",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaClock
                style={{ fontSize: "1.75rem", color: "var(--success)" }}
              />
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="dark-card bento-item-1x1 animate-fade-in-up">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <p
                className="text-muted mb-2"
                style={{ fontSize: "0.875rem", fontWeight: 500 }}
              >
                Doanh thu hôm nay
              </p>
              <h2
                className="mb-0"
                style={{
                  fontSize: "2.5rem",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg, var(--danger) 0%, #DC2626 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {revenue / 10}M
              </h2>
              <span className="glow-badge success mt-2">+8%</span>
            </div>
            <div
              style={{
                width: "56px",
                height: "56px",
                background: "rgba(239, 68, 68, 0.1)",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaDollarSign
                style={{ fontSize: "1.75rem", color: "var(--danger)" }}
              />
            </div>
          </div>
        </div>

        {/* Performance Chart Card */}
        <div className="dark-card bento-item-2x1 animate-fade-in-up">
          <div className="mb-3">
            <h5 className="mb-1" style={{ fontWeight: 600 }}>
              Doanh thu theo giờ
            </h5>
            <small className="text-muted">Biểu đồ thời gian thực</small>
          </div>
          <div
            style={{
              height: "120px",
              background:
                "linear-gradient(180deg, rgba(245, 158, 11, 0.1) 0%, transparent 100%)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "flex-end",
              gap: "4px",
              padding: "1rem",
            }}
          >
            {[40, 65, 45, 80, 60, 90, 70, 85, 95, 75, 88, 92].map(
              (height, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: `${height}%`,
                    background:
                      "linear-gradient(180deg, var(--amber-400) 0%, var(--amber-600) 100%)",
                    borderRadius: "4px 4px 0 0",
                    transition: "all 0.3s ease",
                    opacity: 0.8,
                    animation: `fadeInUp 0.5s ease-out ${i * 0.05}s forwards`,
                    animationFillMode: "backwards",
                  }}
                />
              )
            )}
          </div>
        </div>

        {/* Table Map Visualization */}
        <div className="dark-card bento-item-2x2 animate-fade-in-up">
          <div className="mb-3">
            <h5 className="mb-1" style={{ fontWeight: 600 }}>
              Sơ đồ bàn
            </h5>
            <small className="text-muted">Nhấp để xem chi tiết</small>
          </div>
          <div className="table-grid">
            {tables.map((table) => (
              <div
                key={table.id}
                className={`table-cell ${table.status}`}
                onClick={() => console.log("Table clicked:", table.id)}
              >
                <div className="table-number">{table.id}</div>
                <div className="table-status">
                  {table.status === "occupied"
                    ? "Đang dùng"
                    : table.status === "reserved"
                    ? "Đã đặt"
                    : "Trống"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats - Morning Shift */}
        <div className="dark-card bento-item-1x1 animate-fade-in-up">
          <div className="text-center">
            <div
              style={{
                width: "64px",
                height: "64px",
                background: "rgba(6, 182, 212, 0.1)",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
              }}
            >
              <FaClock style={{ fontSize: "1.75rem", color: "var(--info)" }} />
            </div>
            <h5 className="mb-2" style={{ fontWeight: 600 }}>
              Ca sáng
            </h5>
            <p className="text-muted mb-2">42 khách</p>
            <span className="glow-badge success">Hoàn thành</span>
          </div>
        </div>

        {/* Quick Stats - Reservations */}
        <div className="dark-card bento-item-1x1 animate-fade-in-up">
          <div className="text-center">
            <div
              style={{
                width: "64px",
                height: "64px",
                background: "rgba(245, 158, 11, 0.1)",
                borderRadius: "50%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem",
              }}
            >
              <FaCalendar
                style={{ fontSize: "1.75rem", color: "var(--warning)" }}
              />
            </div>
            <h5 className="mb-2" style={{ fontWeight: 600 }}>
              Đặt bàn
            </h5>
            <p className="text-muted mb-2">15 bàn hôm nay</p>
            <span className="glow-badge warning">Đang xử lý</span>
          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="fab-container">
        <button className="fab" title="Thêm đơn hàng">
          <FaPlus />
        </button>
        <button className="fab" title="Thông báo">
          <FaBell />
        </button>
        <button className="fab" title="Báo cáo">
          <FaChartLine />
        </button>
      </div>
    </Container>
  );
}

export default StaffDashboard;
